/**
 * 봇짐장수 — 사냥의 결을 고르는 악세사리와 몸에 새기는 무게 각인.
 *
 * 과적 페널티(7.1)를 없애지 않고 **선택지로 만든다.** 무게 한도·흥정·과적 완화·
 * 네임드 보상 중 하나만 착용한다. 4천만 은화 최종품만 네 편의축을 합치고,
 * 그 아래는 같은 가격대의 다른 결을 둬 값이 선택을 대신하지 않게 한다.
 */
import {
  ACCS, ACC_BY_ID, CARRY_SIGILS, accSell, carryBonus, carryRankOf,
} from '../../world/map.js';
import { S } from '../../core/state.js';
import { buyBtn, eqName, fold, guildRingInfo, muted, npcHead, num, sec, split, dealFor, afterTax, taxPct, taxedPrice, withTax } from './common.js';

/**
 * ⚠ **같은 동작은 같은 말로 부른다.** 이 파일만 *"메고 있는 것 · 메는 중 · 벗는다"*
 * 로 혼자 다른 말을 썼다. 게임 전체를 세어보면:
 *
 *     착용 10회   ·   벗기 3회   ·   메다 계열 2회(여기뿐)
 *     해제 3회는 **고른 것을 무르는 것**이다(대장간 선택·대상) — 벗기와 다른 뜻이라 안 섞는다
 *
 * 가방도 몸에 차는 것이라 **착용/벗기**로 맞춘다. 사용자 원칙(*"기본적인 온라인
 * MMORPG 의 룰을 따르는 게 직관성에 좋다"*) 그대로다 — 같은 것을 두 이름으로
 * 부르면 그만큼 더 배워야 한다.
 */
export function render({ y, at }, { embedded = false } = {}) {
  const worn = y.acc || null;
  const ring = y.inv?.equips?.find((e) => e.id === y.inv?.equipped?.ring);
  const cap = y.cap || 0;
  const rank = carryRankOf(y.inv && y.inv.carryRank);
  const permanent = carryBonus(rank);
  const held = (y.inv && y.inv.junk) || {};

  /**
   * ── **위는 지금 상태, 아래는 고르는 곳** ──
   *
   * 이 화면만 `split()` 을 안 쓰고 **한 장짜리 두루마리**로 남아 있었다. 실측:
   *
   *     모바일 1.36화면 · 데스크탑 1.13화면   (가방·상점·강화는 전부 1.00)
   *
   * 그래서 **제일 비싼 가방과 [벗기] 가 접힌 아래에 있었다** — 사고 싶은 것과
   * 지금 할 수 있는 일이 둘 다 안 보이는 자리다. 사용자가 *"선택하고도 한참 밑을
   * 내려야 ... 조악스러운 UI"* 라고 한 그 부류고, 나머지 셋은 그때 고쳤는데
   * **여기만 남았다.**
   *
   * ⚠ 덮개(sticky)로 풀지 않는다 — 목록 위를 덮어 클릭을 삼킨다.
   * 상자를 둘로 가르면 구조적으로 서로를 못 가린다(`common.js` `split` 머리말).
   */
  const wornEffect = worn ? (ACC_BY_ID.get(worn)?.desc || '효과 없음') : '';
  const focus = npcHead()
    + sec('악세사리',
      `<div class="ux-facts"><div class="ux-fact"><small>지금 무게 한도</small><b class="ux-number">${num(cap)}</b></div>`
      + `<div class="ux-fact"><small>착용 악세사리</small><b>${worn ? (y.accName || ACC_BY_ID.get(worn)?.name || worn) : '없음'}</b></div></div>`
      + (worn ? muted(wornEffect) : muted('필요한 효과를 골라 하나 착용한다.')));
  // 현재 악세와 상품을 먼저 본다. 교체 시 자동 판매는 구매 앞에, 세금 안내는 목록 아래 한 번만 둔다.

  let carryList = `<div class="sec"><h3>무게 각인 <em>${rank}/${CARRY_SIGILS.length} · 무게 한도 +${num(permanent)}</em></h3>`
    + muted('I→V 차례로 쓰면 무게 한도가 <b>누적</b> · 죽어도 남고 새 세계에서 초기화');
  for (const d of CARRY_SIGILS) {
    const done = d.rank <= rank;
    const next = d.rank === rank + 1;
    const own = (held[d.key] || 0) > 0;
    carryList += `<div class="item ${done ? 'sel' : ''}"><span class="nm">${d.name}`
      + `<span class="qt">무게 한도 +${num(d.cap)} · ${d.from}${own ? ` · 보유 ${num(held[d.key])}` : ''}</span></span>`
      + `<span class="qt">${taxedPrice(d.price, y)}</span>`
      + (done
        ? '<button disabled>사용 완료</button>'
        : own && next
          ? `<button onclick="act.useCarry('${d.key}')">가방에서 사용</button>`
          : next
            ? buyBtn('사기', `act.buyCarry('${d.key}')`, { at, need: 'buyCarry', y, price: withTax(d.price, y) })
            : `<button disabled>${own ? '앞 단계부터' : `${rank + 1}단계 필요`}</button>`)
      + '</div>';
  }
  carryList += '</div>';

  const kinds = [
    { id: 'carry', name: '무게 한도', pick: (a) => !!a.cap && !a.combined },
    { id: 'trade', name: '잡템 흥정', pick: (a) => !!a.sell && !a.combined },
    { id: 'load', name: '과적 완화', pick: (a) => !!a.loadEase && !a.combined },
    { id: 'hunt', name: '보스 보상', pick: (a) => !!a.eliteBonus && !a.combined },
    { id: 'combined', name: '종합', pick: (a) => !!a.combined },
  ];
  const selectedKind = kinds.some((k) => k.id === S.accKind) ? S.accKind : 'all';
  let list = `<div class="sec"><h3>착용 악세사리 <em>${ACCS.length}종</em></h3>`
    + '<div class="accfilters">'
    + [{ id: 'all', name: '전체' }, ...kinds].map((k) =>
      `<button class="${selectedKind === k.id ? 'sel' : 'ghost'}" onclick="act.accKind('${k.id}')">${k.name}</button>`).join('')
    + '</div>'
    + muted(worn ? '<b>새로 사면 착용 중인 악세사리는 자동으로 팔린다.</b>' : '하나만 소유·착용할 수 있다.');
  for (const kind of kinds) {
    if (selectedKind !== 'all' && selectedKind !== kind.id) continue;
    const rows = ACCS.filter(kind.pick);
    if (!rows.length) continue;
    list += `<h4 class="accgroup">${kind.name} <small>${rows.length}종</small></h4>`;
    for (const a of rows) {
    const on = worn === a.id;
    // 특가는 서버가 정하고 서버가 그 값으로 판다 — 화면은 받은 값을 그대로 쓴다.
    // 정가를 적어두면 실제로 나가는 은화와 달라지고, 살 수 있는 판정도 어긋난다.
    const d = dealFor('acc', a.id);
    const price = d ? d.price : a.price;
      list += `<div class="item ${on ? 'sel' : ''}"><span class="nm">${a.name}${a.combined ? ' <b style="color:var(--gold)">종합형</b>' : ''}${d ? ' <b style="color:var(--gold)">오늘의 특가</b>' : ''}
        <span class="qt">${a.desc}${on ? ' · 착용 중' : ''}</span></span>
      <span class="qt">${taxedPrice(price, y, d ? a.price : 0)}</span>
      ${on
        ? buyBtn(`팔기 ${num(afterTax(accSell(a.id), y))}`, 'act.sellAcc()', { at, need: 'sellAcc' })
        : buyBtn('사기', `act.buyAcc('${a.id}')`, { at, need: 'buyAcc', y, price: withTax(price, y) })}
    </div>`;
    }
  }
  list += `</div>` + muted(`가격은 왕도 세금 ${taxPct(y)}% 포함 · 팔기 금액은 세금을 뺀 수령액`)
    + carryList;
  if (ring || y.inv?.storedAcc) list += `<details class="lore" id="acc-ring"><summary>반지와 보관 악세사리</summary><div class="in">`
    + (ring ? `<div class="kv"><span>착용 반지</span><b>${eqName(ring)}</b></div>` + guildRingInfo(ring, y)
      + muted('반지는 가방의 반지 칸에 착용한다. 악세사리와 함께 착용할 수 있다.') : '')
    + (y.inv?.storedAcc ? muted(`보관 중인 악세: ${ACC_BY_ID.get(y.inv.storedAcc)?.name || '기존 악세'}. 악세 칸이 비면 되돌아온다.`) : '')
    + '</div></details>';

  /**
   * 규칙 설명은 **목록 아래**다. 처음 한 번 읽으면 되는 글이라 위를 차지하면
   * 매번 그만큼 밀려 내려간다 — 위는 **지금 값이 바뀌는 것**만 둔다.
   */
  // Q276 — 한 줄 요약 + [자세히]
  list += fold('악세사리 교체와 판매 규칙', `
    <b>무게 한도 · 잡템 흥정 · 과적 완화 · 네임드/보스 보상</b> 중 하나를 고른다.
    40,000,000 은화 종합형은 네 효과를 모두 갖췄다.
    전투 능력치에는 영향을 주지 않으며, 한 번에 하나만 착용할 수 있다.
    새로 사면 <b>착용 중이던 것은 자동으로 팔린다</b>(구매가의 40%에서 왕도 세금을 뺀 금액).`);

  /**
   * 통합 상점 안에서는 바깥 `shop.split`이 이미 고정 탭+스크롤 목록을 만든다.
   * 여기서 split을 한 번 더 만들면 높이가 정해지지 않은 scroller 안에 scroller가
   * 들어가 휠/손가락 스크롤이 멈춘다. 단독 NPC 화면일 때만 자체 split을 쓴다.
   */
  return embedded ? `<div class="accsummary">${focus}</div>${list}` : split(focus, list);
}
